import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@repo/ui/src/lib/utils';

/**
 * The phone answer to a list table (handoff 13 §7, spec P2.6 §1): one row per
 * record, `line 1 / line 2 / number on the right / actions underneath`.
 *
 * Rendered BESIDE the desktop table, not instead of it — the caller wraps the
 * `<Table>` in `hidden md:block` and drops this in as the `md:hidden` branch.
 * The switch is CSS, never `useIsMobile()`: that hook is `false` on its first
 * render, so a JS branch flashes the table on a phone. The hidden branch is
 * `display:none`, so it is out of the accessibility tree and a screen reader
 * hears each record once.
 *
 * No border or radius of its own: every consumer already has a bordered
 * container holding the table, and both branches live inside it (the shape the
 * invoices list shipped in P2.2). Only the dividers belong to the list.
 */
export function CardList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <ul role="list" className={cn('divide-y divide-surface-200 md:hidden', className)}>
      {children}
    </ul>
  );
}

export function CardRow({
  title,
  titleRight,
  line2,
  line3,
  right,
  actions,
  to,
  onClick,
  label,
}: {
  /** Line 1. Phrasing content only — this renders inside an <a>/<button>. */
  title: ReactNode;
  /** Badge or chip pinned to the end of line 1. `Badge` is a <span>, so it fits. */
  titleRight?: ReactNode;
  /** Line 2, one truncated line of reference facts ("Môn · Người trả phí"). */
  line2?: ReactNode;
  /** Line 3, optional — a second truncated reference line below line 2 (THI-267
   *  Task 15: the students list's class line, "Toán 8 · Tiếng Anh 6 (1/2)").
   *  Falsy (including `null`) renders nothing, same as `line2`. */
  line3?: ReactNode;
  /** The number on the right (amount, total). Tabular, never wraps. */
  right?: ReactNode;
  /**
   * Buttons under the row. Rendered OUTSIDE the link/button on purpose: an
   * interactive element inside another one is invalid HTML, and keeping them
   * siblings means no consumer ever needs a stopPropagation guard for a tap
   * that would otherwise navigate.
   */
  actions?: ReactNode;
  /** Whole-row link target. Mutually exclusive with `onClick`. */
  to?: string;
  /** Whole-row tap handler (e.g. open a sheet). Mutually exclusive with `to`. */
  onClick?: () => void;
  /** Overrides the accessible name when the visible title is not enough. */
  label?: string;
}) {
  const body = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-surface-900">{title}</span>
          {titleRight ? <span className="shrink-0">{titleRight}</span> : null}
        </span>
        {line2 ? <span className="block truncate text-[12px] text-surface-500">{line2}</span> : null}
        {line3 ? <span className="block truncate text-[12px] text-surface-500">{line3}</span> : null}
      </span>
      {right ? (
        <span className="shrink-0 self-center text-right text-sm font-semibold tabular-nums text-surface-900">
          {right}
        </span>
      ) : null}
    </>
  );

  // 44px pinned in px: --spacing is 0.2rem app-wide, so min-h-11 would be 35px.
  const rowClass = 'flex w-full min-h-[44px] items-start gap-3 px-3 py-2 text-left';

  return (
    <li>
      {to ? (
        <Link to={to} aria-label={label} className={cn(rowClass, 'hover:bg-surface-50')}>
          {body}
        </Link>
      ) : onClick ? (
        <button type="button" onClick={onClick} aria-label={label} className={cn(rowClass, 'hover:bg-surface-50')}>
          {body}
        </button>
      ) : (
        <div className={rowClass}>{body}</div>
      )}
      {actions ? <div className="flex flex-wrap items-center justify-end gap-2 px-3 pb-2">{actions}</div> : null}
    </li>
  );
}
